/**
 * Net Annual Benefit (NAB)
 * Formula: annualGrossSavings - annualOpexChange
 * A positive opexChange means extra operating costs introduced by the initiative;
 * a negative value means the initiative also reduces operating costs.
 * Returns null when annualGrossSavings is unknown.
 */
export function calcNetAnnualBenefit(
    annualGrossSavings: number | null,
    annualOpexChange: number | null,
): number | null {
    if (annualGrossSavings == null) return null;
    return annualGrossSavings - (annualOpexChange ?? 0);
}

/**
 * Simple Payback Period (years)
 * Formula: capex / nab
 * Guards against division by zero when nab is 0 or null.
 * Returns null when either input is missing.
 */
export function calcSimplePayback(capex: number | null, nab: number | null): number | null {
    if (capex == null || !nab) return null;
    return capex / nab;
}

/**
 * Return on Investment (%)
 * Formula: (npv / capex) * 100
 * Guards against division by zero when capex is 0 or null.
 * Returns null when either input is missing.
 */
export function calcRoi(npv: number | null, capex: number | null): number | null {
    if (npv == null || !capex) return null;
    return (npv / capex) * 100;
}

/**
 * Net Present Value ($)
 * Formula: Σ(nab / (1 + r)^t) for t = 1..n  −  capex
 * Defaults: projectLifespan = 10 years, discountRate = 8%.
 * Uses standard DCF (Discounted Cash Flow) to express future benefits in today's dollars.
 * Returns null when nab or capex is missing.
 */
export function calcNpv(
    nab: number | null,
    capex: number | null,
    projectLifespan: number | null,
    discountRate: number | null,
): number | null {
    if (nab == null || capex == null) return null;
    const n = projectLifespan ?? 10;
    const r = (discountRate ?? 8) / 100;
    let pv = 0;
    for (let t = 1; t <= n; t += 1) {
        pv += nab / (1 + r) ** t;
    }
    return pv - capex;
}

/**
 * Cost per KPI Unit ($/unit)
 * Formula: capex / expectedAnnualImprovement
 * Answers "how much did we invest per unit of KPI improvement?".
 * Guards against division by zero when expectedAnnualImprovement is 0 or null.
 * Returns null when either input is missing.
 */
export function calcCostPerKpiUnit(
    capex: number | null,
    expectedAnnualImprovement: number | null,
): number | null {
    if (capex == null || !expectedAnnualImprovement) return null;
    return capex / expectedAnnualImprovement;
}

/**
 * Projected Improvement (%)
 * Formula: (expectedAnnualImprovement / baselineValue) * 100
 * Expresses the improvement as a percentage of the current baseline.
 * Guards against division by zero when baselineValue is 0 or null.
 * Returns null when either input is missing.
 */
export function calcProjectedImprovementPct(
    expectedAnnualImprovement: number | null,
    baselineValue: number | null,
): number | null {
    if (expectedAnnualImprovement == null || !baselineValue) return null;
    return (expectedAnnualImprovement / baselineValue) * 100;
}

/**
 * Projected New KPI Value (absolute)
 * Formula: baseline − improvement  (REDUCTION)
 *          baseline + improvement  (INCREASE)
 * Direction determines whether the initiative reduces or raises the KPI.
 * Returns null when either baseline or improvement is missing.
 */
export function calcProjectedNewKpiValue(
    baselineValue: number | null,
    expectedAnnualImprovement: number | null,
    direction: string,
): number | null {
    if (baselineValue == null || expectedAnnualImprovement == null) return null;
    return direction === 'INCREASE'
        ? baselineValue + expectedAnnualImprovement
        : baselineValue - expectedAnnualImprovement;
}
